// src/components/Avaliacao/AvaliacaoForm.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Grid,
  Paper,
  Tooltip
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import {
  criarAvaliacao,
  atualizarAvaliacao,
  buscarAvaliacaoPorDefesa
} from '../../services/avaliacaoService';
import { obterDefesa } from '../../services/defesaService';

/** =============================================
 * 1) Constantes de referência - Alinhadas com backend
 * ============================================*/

// Máximos do Trabalho Escrito (soma = 20)
const MAXIMOS_ESCRITO = {
  tema: 2,
  postura: 2,
  introducao: 2,
  revisao: 3,
  metodologia: 3,
  argumentos: 5,
  conclusoes: 3
};

// Máximos da Defesa Oral (soma = 20)
const MAXIMOS_ORAL = {
  dominioTema: 4,
  clarezaOrganizacao: 3,
  argumentacaoDebate: 4,
  postura: 2,
  respostasJuri: 3,
  capacidadeSintese: 2,
  observanciaTempo: 2
};

// Descrições (tooltips) para cada critério do Trabalho Escrito
const DESCRICOES_ESCRITO = {
  tema: [
    'Relevância e atualidade do tema',
    'Aplicabilidade do tema'
  ],
  introducao: [
    'Articulação clara do problema',
    'Contextualização e relevância',
    'Objetivos claros'
  ],
  revisao: [
    'Síntese crítica da literatura',
    'Integração dos resultados',
    'Qualidade das fontes'
  ],
  metodologia: [
    'Descrição clara do processo',
    'Inovação metodológica',
    'Procedimentos lógicos',
    'Concepção apropriada',
    'Análise de dados'
  ],
  argumentos: [
    'Conceitos coerentes',
    'Argumentação lógica',
    'Diversidade de fontes',
    'Interpretação prática',
    'Clareza e criatividade'
  ],
  conclusoes: [
    'Clareza e conexão com objetivos',
    'Recomendações pertinentes'
  ]
};

// Descrições (tooltips) para cada critério da Defesa Oral
const DESCRICOES_ORAL = {
  dominioTema: ['Domínio do tema apresentado'],
  clarezaOrganizacao: ['Clareza e organização das ideias'],
  argumentacaoDebate: ['Qualidade da argumentação'],
  postura: ['Postura acadêmica'],
  respostasJuri: ['Qualidade das respostas'],
  capacidadeSintese: ['Capacidade de síntese'],
  observanciaTempo: ['Gestão do tempo']
};

/** =============================================
 * Componente AvaliacaoForm
 * ============================================*/
function AvaliacaoForm({ defesaId, onSuccess }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Dados da defesa (opcional)
  const [defesa, setDefesa] = useState(null);

  /** =============================================
   * 1) Identificar o papel do usuário
   * ============================================*/
  const getUserRole = () => {
    if (user?.tipo === 'arguente') return 'arguente';
    if (user?.tipo === 'presidente') return 'presidente';
    if (user?.tipo === 'supervisor') return 'supervisor';
    return null;
  };
  const userRole = getUserRole();

  /** =============================================
   * 2) Estado inicial do formData - Alinhado com backend
   * ============================================*/
  const [formData, setFormData] = useState({
    // Dados gerais da defesa
    defesaId: defesaId || '',
    nomeEstudante: '',
    curso: '',
    grauAcademico: 'Licenciatura',
    tema: '',
    dataDefesa: '',
    // Nomes e títulos (para futura emissão de documentos, etc.)
    presidenteNome: '',
    presidenteTitulo: '',
    arguenteNome: '',
    arguenteTitulo: '',
    supervisorNome: '',
    supervisorTitulo: '',
    diretorCientifico: '',

    // Notas do Arguente - Trabalho Escrito
    arguenteEscritoTema: '',
    arguenteEscritoIntroducao: '',
    arguenteEscritoPostura: '',  
    arguenteEscritoRevisao: '',
    arguenteEscritoMetodologia: '',
    arguenteEscritoArgumentos: '',
    arguenteEscritoConclusoes: '',

    // Notas do Arguente - Defesa Oral
    arguenteOralDominioTema: '',
    arguenteOralClarezaOrganizacao: '',
    arguenteOralArgumentacaoDebate: '',
    arguenteOralPostura: '',
    arguenteOralRespostasJuri: '',
    arguenteOralCapacidadeSintese: '',
    arguenteOralObservanciaTempo: '',

    // Notas do Presidente - Trabalho Escrito
    presidenteEscritoTema: '',
    presidenteEscritoIntroducao: '',
    presidenteEscritoPostura: '', 
    presidenteEscritoRevisao: '',
    presidenteEscritoMetodologia: '',
    presidenteEscritoArgumentos: '',
    presidenteEscritoConclusoes: '',

    // Notas do Presidente - Defesa Oral
    presidenteOralDominioTema: '',
    presidenteOralClarezaOrganizacao: '',
    presidenteOralArgumentacaoDebate: '',
    presidenteOralPostura: '',
    presidenteOralRespostasJuri: '',
    presidenteOralCapacidadeSintese: '',
    presidenteOralObservanciaTempo: '',

    // Notas do Supervisor - Trabalho Escrito
    supervisorEscritoTema: '',
    supervisorEscritoIntroducao: '',
    supervisorEscritoPostura: '', 
    supervisorEscritoRevisao: '',
    supervisorEscritoMetodologia: '',
    supervisorEscritoArgumentos: '',
    supervisorEscritoConclusoes: '',

    // Notas do Supervisor - Defesa Oral
    supervisorOralDominioTema: '',
    supervisorOralClarezaOrganizacao: '',
    supervisorOralArgumentacaoDebate: '',
    supervisorOralPostura: '',
    supervisorOralRespostasJuri: '',
    supervisorOralCapacidadeSintese: '',
    supervisorOralObservanciaTempo: '',

    // Totais calculados (podemos armazenar ou calcular "on the fly")
    arguenteEscritoTotal: 0,
    arguenteOralTotal: 0,
    presidenteEscritoTotal: 0,
    presidenteOralTotal: 0,
    supervisorEscritoTotal: 0,
    supervisorOralTotal: 0,
    notaTrabalhoEscrito: 0,
    notaDefesaOral: 0,
    mediaFinal: 0
  });

  /** =============================================
   * 3) Carregar dados da defesa e da avaliação (se existir)
   * ============================================*/
  useEffect(() => {
    const carregarDados = async () => {
      if (!defesaId) return;

      setLoading(true);
      try {
        // 1) Carrega dados da Defesa
        const resDefesa = await obterDefesa(defesaId);
        const dataDefesa = resDefesa.data;
        setDefesa(dataDefesa);

        // Ajusta formData com informações gerais
        setFormData((prev) => ({
          ...prev,
          defesaId,
          nomeEstudante:
            dataDefesa?.monografia?.projeto?.proposta?.estudante?.nome || '',
          curso: dataDefesa?.monografia?.projeto?.proposta?.curso || '',
          grauAcademico:
            dataDefesa?.monografia?.projeto?.proposta?.grauAcademico ||
            'Licenciatura',
          tema: dataDefesa?.monografia?.projeto?.proposta?.titulo || '',
          dataDefesa: dataDefesa?.data_defesa || '',
          presidenteNome: dataDefesa?.presidente?.usuario?.nome || '',
          arguenteNome: dataDefesa?.arguente?.usuario?.nome || '',
          supervisorNome: dataDefesa?.supervisor?.usuario?.nome || '',
          diretorCientifico: dataDefesa?.diretorCientifico || ''
        }));

        // 2) Verifica se já existe avaliação salva
        const avaliacaoExistente = await buscarAvaliacaoPorDefesa(defesaId);
        if (avaliacaoExistente) {
          // Preenche o formData com o que já tiver no backend
          setFormData((prev) => ({
            ...prev,
            // Mapeia o que vier do backend para o state local:
            ...avaliacaoExistente
          }));
        }
      } catch (err) {
        console.error('Erro ao carregar dados da defesa/avaliação:', err);
        setError('Erro ao carregar dados da avaliação.');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [defesaId]);

  /** =============================================
   * 4) Lógica para permitir edição somente do papel atual
   * ============================================*/
  function podeEditarCampo(nomeCampo) {
    // Se não tiver userRole, não pode editar nada
    if (!userRole) return false;

    // Se userRole = 'arguente', só pode editar campos que começam com arguenteEscrito ou arguenteOral
    // Se userRole = 'presidente', só pode editar campos que começam com presidenteEscrito ou presidenteOral
    // Se userRole = 'supervisor', só pode editar campos que começam com supervisorEscrito ou supervisorOral

    if (userRole === 'arguente') {
      return (
        nomeCampo.startsWith('arguenteEscrito') ||
        nomeCampo.startsWith('arguenteOral')
      );
    }

    if (userRole === 'presidente') {
      return (
        nomeCampo.startsWith('presidenteEscrito') ||
        nomeCampo.startsWith('presidenteOral')
      );
    }

    if (userRole === 'supervisor') {
      return (
        nomeCampo.startsWith('supervisorEscrito') ||
        nomeCampo.startsWith('supervisorOral')
      );
    }

    return false;
  }

  /** =============================================
   * 5) handleChange: atualizar formData somente se podeEditarCampo = true
   * ============================================*/
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!podeEditarCampo(name)) {
      return; // Ignora se não pode editar
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  /** =============================================
   * 6) Função para calcular totais - Alinhada com backend
   * ============================================*/
  function calcularTotais() {
    // Função auxiliar que soma os critérios respeitando os máximos
    function somarCriterios(notas, maximos) {
      let soma = 0;
      Object.keys(maximos).forEach((criterio) => {
        const valor = parseFloat(notas[criterio]) || 0;
        const maximo = maximos[criterio];
        // Garante que não excede o máximo e não seja negativo
        soma += Math.min(Math.max(valor, 0), maximo);
      });
      // Arredonda para 1 casa decimal
      return Math.round(soma * 10) / 10;
    }

    // ----------------- ARGUENTE -----------------
    // Trabalho Escrito - Arguente
    const arguenteEscrito = {
      tema: formData.arguenteEscritoTema,
      introducao: formData.arguenteEscritoIntroducao,
      postura: formData.arguenteEscritoPostura, 
      revisao: formData.arguenteEscritoRevisao,
      metodologia: formData.arguenteEscritoMetodologia,
      argumentos: formData.arguenteEscritoArgumentos,
      conclusoes: formData.arguenteEscritoConclusoes
    };
    const arguenteEscritoTotal = somarCriterios(arguenteEscrito, MAXIMOS_ESCRITO);

    // Defesa Oral - Arguente
    const arguenteOral = {
      dominioTema: formData.arguenteOralDominioTema,
      clarezaOrganizacao: formData.arguenteOralClarezaOrganizacao,
      argumentacaoDebate: formData.arguenteOralArgumentacaoDebate,
      postura: formData.arguenteOralPostura,
      respostasJuri: formData.arguenteOralRespostasJuri,
      capacidadeSintese: formData.arguenteOralCapacidadeSintese,
      observanciaTempo: formData.arguenteOralObservanciaTempo
    };
    const arguenteOralTotal = somarCriterios(arguenteOral, MAXIMOS_ORAL);

    // ----------------- PRESIDENTE -----------------
    // Trabalho Escrito - Presidente
    const presidenteEscrito = {
      tema: formData.presidenteEscritoTema,
      introducao: formData.presidenteEscritoIntroducao,
      postura: formData.presidenteEscritoPostura, 
      revisao: formData.presidenteEscritoRevisao,
      metodologia: formData.presidenteEscritoMetodologia,
      argumentos: formData.presidenteEscritoArgumentos,
      conclusoes: formData.presidenteEscritoConclusoes
    };
    const presidenteEscritoTotal = somarCriterios(presidenteEscrito, MAXIMOS_ESCRITO);

    // Defesa Oral - Presidente
    const presidenteOral = {
      dominioTema: formData.presidenteOralDominioTema,
      clarezaOrganizacao: formData.presidenteOralClarezaOrganizacao,
      argumentacaoDebate: formData.presidenteOralArgumentacaoDebate,
      postura: formData.presidenteOralPostura,
      respostasJuri: formData.presidenteOralRespostasJuri,
      capacidadeSintese: formData.presidenteOralCapacidadeSintese,
      observanciaTempo: formData.presidenteOralObservanciaTempo
    };
    const presidenteOralTotal = somarCriterios(presidenteOral, MAXIMOS_ORAL);

    // ----------------- SUPERVISOR -----------------
    // Trabalho Escrito - Supervisor
    const supervisorEscrito = {
      tema: formData.supervisorEscritoTema,
      introducao: formData.supervisorEscritoIntroducao,
      postura: formData.supervisorEscritoPostura,
      revisao: formData.supervisorEscritoRevisao,
      metodologia: formData.supervisorEscritoMetodologia,
      argumentos: formData.supervisorEscritoArgumentos,
      conclusoes: formData.supervisorEscritoConclusoes
    };
    const supervisorEscritoTotal = somarCriterios(supervisorEscrito, MAXIMOS_ESCRITO);

    // Defesa Oral - Supervisor
    const supervisorOral = {
      dominioTema: formData.supervisorOralDominioTema,
      clarezaOrganizacao: formData.supervisorOralClarezaOrganizacao,
      argumentacaoDebate: formData.supervisorOralArgumentacaoDebate,
      postura: formData.supervisorOralPostura,
      respostasJuri: formData.supervisorOralRespostasJuri,
      capacidadeSintese: formData.supervisorOralCapacidadeSintese,
      observanciaTempo: formData.supervisorOralObservanciaTempo
    };
    const supervisorOralTotal = somarCriterios(supervisorOral, MAXIMOS_ORAL);

    // ----------------- CÁLCULO DAS NOTAS PONDERADAS -----------------
    // Exemplo de ponderação:
    // - Arguente = 70%
    // - Presidente = 20%
    // - Supervisor = 10%
    const notaTrabalhoEscrito = Math.round(
      (
        arguenteEscritoTotal * 0.7 +
        presidenteEscritoTotal * 0.2 +
        supervisorEscritoTotal * 0.1
      ) * 10
    ) / 10;

    const notaDefesaOral = Math.round(
      (
        arguenteOralTotal * 0.7 +
        presidenteOralTotal * 0.2 +
        supervisorOralTotal * 0.1
      ) * 10
    ) / 10;

    const mediaFinal = Math.round(((notaTrabalhoEscrito + notaDefesaOral) / 2) * 10) / 10;

    return {
      arguenteEscritoTotal,
      arguenteOralTotal,
      presidenteEscritoTotal,
      presidenteOralTotal,
      supervisorEscritoTotal,
      supervisorOralTotal,
      notaTrabalhoEscrito,
      notaDefesaOral,
      mediaFinal
    };
  }

  /** =============================================
   * 7) handleSubmit - Salvar ou atualizar avaliação
   * ============================================*/
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Verifica se já existe alguma avaliação
      const avaliacaoExistente = await buscarAvaliacaoPorDefesa(defesaId);

      // Calcula os totais antes de salvar
      const totais = calcularTotais();

      // Dados gerais (sempre enviados)
      const dadosBase = {
        defesaId: parseInt(defesaId, 10),
        nomeEstudante: formData.nomeEstudante,
        curso: formData.curso,
        grauAcademico: formData.grauAcademico,
        tema: formData.tema,
        dataDefesa: formData.dataDefesa,
        presidenteNome: formData.presidenteNome,
        presidenteTitulo: formData.presidenteTitulo,
        arguenteNome: formData.arguenteNome,
        arguenteTitulo: formData.arguenteTitulo,
        supervisorNome: formData.supervisorNome,
        supervisorTitulo: formData.supervisorTitulo,
        diretorCientifico: formData.diretorCientifico
      };

      // Campos que o avaliador atual pode alterar
      let notasAvaliador = {};

      if (userRole === 'arguente') {
        notasAvaliador = {
          arguenteEscritoTema: formData.arguenteEscritoTema,
          arguenteEscritoPostura: formData.arguenteEscritoPostura,
          arguenteEscritoIntroducao: formData.arguenteEscritoIntroducao,
          arguenteEscritoRevisao: formData.arguenteEscritoRevisao,
          arguenteEscritoMetodologia: formData.arguenteEscritoMetodologia,
          arguenteEscritoArgumentos: formData.arguenteEscritoArgumentos,
          arguenteEscritoConclusoes: formData.arguenteEscritoConclusoes,

          arguenteOralDominioTema: formData.arguenteOralDominioTema,
          arguenteOralClarezaOrganizacao: formData.arguenteOralClarezaOrganizacao,
          arguenteOralArgumentacaoDebate: formData.arguenteOralArgumentacaoDebate,
          arguenteOralPostura: formData.arguenteOralPostura,
          arguenteOralRespostasJuri: formData.arguenteOralRespostasJuri,
          arguenteOralCapacidadeSintese: formData.arguenteOralCapacidadeSintese,
          arguenteOralObservanciaTempo: formData.arguenteOralObservanciaTempo
        };
      } else if (userRole === 'presidente') {
        notasAvaliador = {
          presidenteEscritoTema: formData.presidenteEscritoTema,
          presidenteEscritoPostura: formData.presidenteEscritoPostura,
          presidenteEscritoIntroducao: formData.presidenteEscritoIntroducao,
          presidenteEscritoRevisao: formData.presidenteEscritoRevisao,
          presidenteEscritoMetodologia: formData.presidenteEscritoMetodologia,
          presidenteEscritoArgumentos: formData.presidenteEscritoArgumentos,
          presidenteEscritoConclusoes: formData.presidenteEscritoConclusoes,

          presidenteOralDominioTema: formData.presidenteOralDominioTema,
          presidenteOralClarezaOrganizacao: formData.presidenteOralClarezaOrganizacao,
          presidenteOralArgumentacaoDebate: formData.presidenteOralArgumentacaoDebate,
          presidenteOralPostura: formData.presidenteOralPostura,
          presidenteOralRespostasJuri: formData.presidenteOralRespostasJuri,
          presidenteOralCapacidadeSintese: formData.presidenteOralCapacidadeSintese,
          presidenteOralObservanciaTempo: formData.presidenteOralObservanciaTempo
        };
      } else if (userRole === 'supervisor') {
        notasAvaliador = {
          supervisorEscritoTema: formData.supervisorEscritoTema,
          supervisorEscritoPostura: formData.supervisorEscritoPostura,
          supervisorEscritoIntroducao: formData.supervisorEscritoIntroducao,
          supervisorEscritoRevisao: formData.supervisorEscritoRevisao,
          supervisorEscritoMetodologia: formData.supervisorEscritoMetodologia,
          supervisorEscritoArgumentos: formData.supervisorEscritoArgumentos,
          supervisorEscritoConclusoes: formData.supervisorEscritoConclusoes,

          supervisorOralDominioTema: formData.supervisorOralDominioTema,
          supervisorOralClarezaOrganizacao: formData.supervisorOralClarezaOrganizacao,
          supervisorOralArgumentacaoDebate: formData.supervisorOralArgumentacaoDebate,
          supervisorOralPostura: formData.supervisorOralPostura,
          supervisorOralRespostasJuri: formData.supervisorOralRespostasJuri,
          supervisorOralCapacidadeSintese: formData.supervisorOralCapacidadeSintese,
          supervisorOralObservanciaTempo: formData.supervisorOralObservanciaTempo
        };
      }

      // Monta o objeto final a ser enviado ao backend
      const payload = {
        ...dadosBase,
        ...avaliacaoExistente, // Carrega o que já existia para não sobrescrever outros campos
        ...notasAvaliador,
        ...totais // incluiu os campos: arguenteEscritoTotal, arguenteOralTotal, ...
      };

      // Decide se cria ou atualiza
      let response;
      if (avaliacaoExistente?.id) {
        response = await atualizarAvaliacao(avaliacaoExistente.id, payload);
      } else {
        response = await criarAvaliacao(payload);
      }

      setSuccess(true);
      if (onSuccess) onSuccess();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao salvar avaliação:', err);
      setError(err.response?.data?.message || 'Erro ao salvar avaliação.');
    } finally {
      setLoading(false);
    }
  };

  /** =============================================
   * 8) Funções de renderização dos campos
   * ============================================*/
  function renderCamposEscrito(avaliador) {
    // avaliador: 'arguente', 'presidente', 'supervisor'
    const podeEditar = userRole === avaliador;

    // Vamos mapear cada critério: tema, introducao, revisao...
    return (
      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: podeEditar ? 'transparent' : '#f5f5f5'
        }}
      >
        <Typography variant="h6" gutterBottom>
          {`Trabalho Escrito (${avaliador})`}
          {!podeEditar && ' (Somente Leitura)'}
        </Typography>

        <Grid container spacing={2}>
          {Object.entries(MAXIMOS_ESCRITO).map(([criterio, maximo]) => {
            // Ex: criterio = 'tema', maximo = 2
            // Montamos o nome do campo no formData:
            // Ex: arguente + 'Escrito' + 'Tema' => arguenteEscritoTema
            // Precisamos transformar a 1ª letra do criterio em maiúscula?
            // 'tema' -> 'Tema'; 'introducao' -> 'Introducao'
            const nomeCampo =
              avaliador +
              'Escrito' +
              criterio.charAt(0).toUpperCase() +
              criterio.slice(1);

            const descricao = DESCRICOES_ESCRITO[criterio]?.join('\n') || '';

            return (
              <Grid item xs={12} sm={6} md={4} key={nomeCampo}>
                <Tooltip title={descricao} arrow placement="top">
                  <TextField
                    fullWidth
                    label={`${criterio.toUpperCase()} (máx: ${maximo})`}
                    name={nomeCampo}
                    type="number"
                    value={formData[nomeCampo] || ''}
                    onChange={handleChange}
                    disabled={!podeEditar}
                    inputProps={{
                      min: 0,
                      max: maximo,
                      step: 0.1
                    }}
                    helperText={
                      podeEditar
                        ? `Nota entre 0 e ${maximo}`
                        : 'Sem permissão para editar'
                    }
                  />
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  }

  function renderCamposOral(avaliador) {
    const podeEditar = userRole === avaliador;

    return (
      <Paper
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: podeEditar ? 'transparent' : '#f5f5f5'
        }}
      >
        <Typography variant="h6" gutterBottom>
          {`Defesa Oral (${avaliador})`}
          {!podeEditar && ' (Somente Leitura)'}
        </Typography>

        <Grid container spacing={2}>
          {Object.entries(MAXIMOS_ORAL).map(([criterio, maximo]) => {
            // Montar o nome do campo no formData, ex: arguente + 'Oral' + 'DominioTema'
            const nomeCampo =
              avaliador +
              'Oral' +
              criterio.charAt(0).toUpperCase() +
              criterio.slice(1);

            const descricao = DESCRICOES_ORAL[criterio]?.join('\n') || '';
            // Ajusta o label removendo a camelCase:
            // ex: criterio = 'dominioTema' -> label = 'dominio Tema (máx: X)'
            // Se preferir, podemos usar regex ou algo mais elaborado para exibir de forma mais legível
            return (
              <Grid item xs={12} sm={6} md={4} key={nomeCampo}>
                <Tooltip title={descricao} arrow placement="top">
                  <TextField
                    fullWidth
                    label={`${criterio} (máx: ${maximo})`}
                    name={nomeCampo}
                    type="number"
                    value={formData[nomeCampo] || ''}
                    onChange={handleChange}
                    disabled={!podeEditar}
                    inputProps={{
                      min: 0,
                      max: maximo,
                      step: 0.1
                    }}
                    helperText={
                      podeEditar
                        ? `Nota entre 0 e ${maximo}`
                        : 'Sem permissão para editar'
                    }
                  />
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  }

  /** =============================================
   * 9) Renderização dos resultados finais
   * ============================================*/
  function renderResultados() {
    const totais = calcularTotais();
    return (
      <Paper sx={{ p: 2, backgroundColor: '#2196f3', color: '#fff' }}>
        <Typography variant="h6" gutterBottom>
          Resultados
        </Typography>
        <Grid container spacing={2}>
          {/* Totais de cada avaliador (Escrito + Oral) */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total (Arguente)"
              value={
                isNaN(totais.arguenteEscritoTotal + totais.arguenteOralTotal)
                  ? ''
                  : totais.arguenteEscritoTotal + totais.arguenteOralTotal
              }
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total (Presidente)"
              value={
                isNaN(totais.presidenteEscritoTotal + totais.presidenteOralTotal)
                  ? ''
                  : totais.presidenteEscritoTotal + totais.presidenteOralTotal
              }
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total (Supervisor)"
              value={
                isNaN(totais.supervisorEscritoTotal + totais.supervisorOralTotal)
                  ? ''
                  : totais.supervisorEscritoTotal + totais.supervisorOralTotal
              }
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' }
              }}
            />
          </Grid>

          {/* Notas finais (ponderadas) */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nota Trabalho Escrito"
              value={isNaN(totais.notaTrabalhoEscrito) ? '' : totais.notaTrabalhoEscrito}
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nota Defesa Oral"
              value={isNaN(totais.notaDefesaOral) ? '' : totais.notaDefesaOral}
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Média Final"
              value={isNaN(totais.mediaFinal) ? '' : totais.mediaFinal}
              disabled
              sx={{
                '& .MuiInputBase-root': { color: '#fff' },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.5)'
                },
                '& .MuiInputLabel-root': { color: '#fff' },
                '& input': { fontWeight: 'bold' }
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  }

  /** =============================================
   * 10) Render Principal
   * ============================================*/
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Avaliação de Defesa
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Avaliação salva com sucesso!
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* ------------------- DADOS DA DEFESA ------------------- */}
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: '#2196f3',
                  color: '#fff'
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Informações da Defesa
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Estudante"
                      value={formData.nomeEstudante || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tema"
                      value={formData.tema || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Curso"
                      value={formData.curso || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Grau Acadêmico"
                      value={formData.grauAcademico || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Data da Defesa"
                      value={
                        formData.dataDefesa
                          ? new Date(formData.dataDefesa).toLocaleString()
                          : ''
                      }
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Nome do Presidente"
                      value={formData.presidenteNome || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Nome do Arguente"
                      value={formData.arguenteNome || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Nome do Supervisor"
                      value={formData.supervisorNome || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Diretor Científico"
                      value={formData.diretorCientifico || ''}
                      disabled
                      sx={{
                        '& .MuiInputBase-root': {
                          color: '#fff',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.5)'
                          }
                        },
                        '& .MuiInputLabel-root': { color: '#fff' }
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* -------------- CAMPOS DE AVALIAÇÃO -------------- */}
            <Grid item xs={12}>
              {/* Arguente: Trabalho Escrito + Defesa Oral */}
              {renderCamposEscrito('arguente')}
              {renderCamposOral('arguente')}

              {/* Presidente: Trabalho Escrito + Defesa Oral */}
              {renderCamposEscrito('presidente')}
              {renderCamposOral('presidente')}

              {/* Supervisor: Trabalho Escrito + Defesa Oral */}
              {renderCamposEscrito('supervisor')}
              {renderCamposOral('supervisor')}
            </Grid>

            {/* -------------- RESULTADOS -------------- */}
            <Grid item xs={12}>
              {renderResultados()}
            </Grid>
          </Grid>

          {/* Botão de Salvar */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !userRole}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Salvando...' : 'Salvar Avaliação'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

AvaliacaoForm.propTypes = {
  defesaId: PropTypes.number,
  onSuccess: PropTypes.func
};

export default AvaliacaoForm;
